<template>
  <div class="ew-donut" role="img" :aria-label="ariaLabel">
    <div class="ew-donut__empty" v-if="!items || items.length === 0">
      {{ emptyText }}
    </div>
    <template v-else>
      <div class="ew-donut__ring-wrap">
        <svg class="ew-donut__svg" viewBox="0 0 200 200">
          <circle class="ew-donut__track" cx="100" cy="100" :r="radius" fill="none" :stroke-width="strokeWidth" />
          <g transform="rotate(-90 100 100)">
            <circle
              v-for="(item, i) in items"
              :key="item.label"
              class="ew-donut__segment"
              :class="{ 'is-hovered': hoveredIndex === i }"
              cx="100"
              cy="100"
              :r="radius"
              fill="none"
              :stroke="colorFor(item.label)"
              :stroke-width="strokeWidth"
              :stroke-dasharray="dashArray(item)"
              :stroke-dashoffset="dashOffset(i)"
              @mouseenter="hoveredIndex = i"
              @mouseleave="hoveredIndex = null"
              @focus="hoveredIndex = i"
              @blur="hoveredIndex = null"
              tabindex="0"
            ><title>{{ item.label }}: {{ item.value }} ({{ pct(item.value) }}%)</title></circle>
          </g>
        </svg>
        <div class="ew-donut__center">
          <span class="ew-donut__center-value">{{ total }}</span>
          <span class="ew-donut__center-label">envíos</span>
        </div>
      </div>

      <ul class="ew-donut__legend">
        <li
          v-for="(item, i) in items"
          :key="item.label"
          class="ew-donut__legend-row"
          :class="{ 'is-hovered': hoveredIndex === i }"
          @mouseenter="hoveredIndex = i"
          @mouseleave="hoveredIndex = null"
        >
          <span class="ew-donut__swatch" :style="{ background: colorFor(item.label) }"></span>
          <span class="ew-donut__legend-label">{{ item.label }}</span>
          <span class="ew-donut__legend-pct">{{ pct(item.value) }}%</span>
          <span class="ew-donut__legend-value">{{ item.value }}</span>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import { colorForCarrier } from '../../commons/chartColors'

export default {
  name: 'DonutChart',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    emptyText: {
      type: String,
      default: 'Sin datos en este periodo'
    }
  },
  data () {
    return {
      radius: 74,
      strokeWidth: 32,
      hoveredIndex: null
    }
  },
  computed: {
    circumference () {
      return 2 * Math.PI * this.radius
    },
    total () {
      return this.items.reduce((sum, item) => sum + item.value, 0)
    },
    ariaLabel () {
      if (!this.items || this.items.length === 0) return this.emptyText
      return 'Distribución por transportista: ' + this.items.map((item) => `${item.label} ${item.value} (${this.pct(item.value)}%)`).join(', ')
    }
  },
  methods: {
    colorFor (label) {
      return colorForCarrier(label)
    },
    pct (value) {
      if (!this.total) return 0
      return Math.round((value / this.total) * 100)
    },
    arcLength (item) {
      return (item.value / this.total) * this.circumference
    },
    dashArray (item) {
      // 2px de hueco (en unidades del viewBox) entre rebanadas, el mismo
      // "surface gap" que separa barras adyacentes.
      const gap = 2
      const len = Math.max(this.arcLength(item) - gap, 0)
      return `${len} ${this.circumference - len}`
    },
    dashOffset (index) {
      let offset = 0
      for (let i = 0; i < index; i++) {
        offset += this.arcLength(this.items[i])
      }
      return -offset
    }
  }
}
</script>

<style scoped lang="scss">
@import "../../assets/styles/app-tokens";

.ew-donut {
  @include app-tokens;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.5rem;
}

.ew-donut__ring-wrap {
  position: relative;
  width: 10rem;
  height: 10rem;
  flex: 0 0 auto;
}

.ew-donut__svg {
  width: 100%;
  height: 100%;
}

.ew-donut__track {
  stroke: var(--app-surface-muted);
}

.ew-donut__segment {
  cursor: pointer;
  transition: opacity 0.15s ease;
  outline: none;

  &.is-hovered { opacity: 0.85; }
}

.ew-donut__center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.ew-donut__center-value {
  font-family: var(--app-font);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--app-navy);
}

.ew-donut__center-label {
  font-family: var(--app-font);
  font-size: 0.72rem;
  color: var(--app-muted);
}

.ew-donut__legend {
  list-style: none;
  margin: 0;
  padding: 0;
  flex: 1 1 12rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.ew-donut__legend-row {
  display: grid;
  grid-template-columns: 0.6rem 1fr auto auto;
  align-items: center;
  gap: 0.6rem;
  border-radius: var(--app-radius-sm);
  padding: 0.15rem 0.3rem;

  &.is-hovered { background: var(--app-surface-muted); }
}

.ew-donut__swatch {
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  flex: 0 0 auto;
}

.ew-donut__legend-label {
  font-family: var(--app-font);
  font-size: 0.82rem;
  color: var(--app-navy-secondary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ew-donut__legend-pct {
  font-family: var(--app-font);
  font-size: 0.78rem;
  color: var(--app-muted);
}

.ew-donut__legend-value {
  font-family: var(--app-font);
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--app-navy);
  text-align: right;
}

.ew-donut__empty {
  font-family: var(--app-font);
  font-size: 0.85rem;
  color: var(--app-muted);
  padding: 0.75rem 0;
}
</style>



// WEBPACK FOOTER //
// src/components/charts/DonutChart.vue